import { logger } from "@services/logger/logger.service";

/**
 * A message that has been accepted by the backend (msg/send returned ok) but
 * whose assistant answer we have not yet shown. Persisted so that closing the
 * task pane mid-generation doesn't lose the answer: on the next open we pick the
 * poll back up from `baselineAnswerMsgId` instead of leaving the thread stuck on
 * a trailing user message with no reply and no spinner.
 */
export interface IPendingAnswer
{
    threadId: string;
    // Newest assistant message that existed *before* this send, so the resumed
    // poll can tell the fresh answer from the previous one.
    baselineAnswerMsgId: string | null;
    createdAt: number;
}

const STORAGE_KEY = 'r2_pending_answer';

// Drop a marker once it is older than the msg/send answer-poll timeout plus a
// little slack: past that the backend is not going to produce an answer and a
// resumed poll would just spin until it times out again.
const MAX_AGE_MS = 7 * 60 * 1000;

type PendingAnswerMap = Record<string, IPendingAnswer>;

/**
 * localStorage-backed, keyed by thread id so every open document keeps its own
 * marker. Stale entries are pruned on every read and write.
 */
export class OfficeChatPendingAnswerStore
{
    public read(threadId: string): IPendingAnswer | null
    {
        const map = this.readMap();
        const entry = map[ threadId ];
        return entry && !this.isStale(entry) ? entry : null;
    }

    public write(entry: IPendingAnswer): void
    {
        const map = this.readMap();
        map[ entry.threadId ] = entry;
        this.writeMap(map);
    }

    public clear(threadId: string): void
    {
        const map = this.readMap();
        if (map[ threadId ])
        {
            delete map[ threadId ];
            this.writeMap(map);
        }
    }

    private isStale(entry: IPendingAnswer): boolean
    {
        return Date.now() - entry.createdAt > MAX_AGE_MS;
    }

    private readMap(): PendingAnswerMap
    {
        try
        {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw)
            {
                return {};
            }

            const parsed = JSON.parse(raw) as PendingAnswerMap;
            const pruned: PendingAnswerMap = {};
            for (const [ threadId, entry ] of Object.entries(parsed))
            {
                if (entry && !this.isStale(entry))
                {
                    pruned[ threadId ] = entry;
                }
            }
            return pruned;
        }
        catch (e)
        {
            logger.error('PendingAnswerStore: failed to read', e);
            return {};
        }
    }

    private writeMap(map: PendingAnswerMap): void
    {
        try
        {
            if (Object.keys(map).length === 0)
            {
                localStorage.removeItem(STORAGE_KEY);
                return;
            }
            localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
        }
        catch (e)
        {
            logger.error('PendingAnswerStore: failed to write', e);
        }
    }
}
